import { computed, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface PasswordState {
  oldPassword?: Ref<string>;
  password?: Ref<string>;
}

interface PasswordRules {
  confirmPasswordRules: Ref<Array<(v: string) => boolean | string>>;
  differentPwdRules: Ref<Array<(v: string) => boolean | string>>;
  oldPwdRules: Ref<Array<(v: string) => boolean | string>>;
  pwdRules: Ref<Array<(v: string) => boolean | string>>;
  otpRules: Ref<Array<(v: string) => boolean | string>>;
  recommendPasswordRules: string;
  requiredRule: (messagePath: string) => (v: string) => boolean | string;
  emailLoginRules: (
    translationKey?: string
  ) => Array<(v: string) => boolean | string>;
}

const RECOMMENDED_PASSWORD_RULES =
  'minlength: 10; required: lower; required: upper; required: digit; required: special;';

export const usePasswordRules = (state: PasswordState = {}): PasswordRules => {
  const { t } = useI18n();

  // Basic required rule
  const requiredRule = (messagePath: string) => (v: string) =>
    !!v || t(messagePath);

  // Password structure rule
  const structureRule = (v: string) =>
    /(?=(.*[0-9]))(?=.*[!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?])(?=.*[a-z])(?=(.*[A-Z]))(?=(.*)).{10,}/.test(
      v
    ) || t('accountHandling.changePassword.rules.passwordFormat');

  // Match password confirmation rule with first password field
  const confirmPasswordRules = computed(() => [
    requiredRule('accountHandling.changePassword.rules.confirmationRepeat'),
    (v: string) =>
      v === state.password?.value ||
      t('accountHandling.changePassword.rules.confirmationMatch')
  ]);

  // Different password rule compared to old password
  const differentPwdRules = computed(() => [
    ...pwdRules.value,
    (v: string) =>
      v !== state.oldPassword?.value ||
      t('accountHandling.changePassword.rules.mustBeDifferent')
  ]);

  const oldPwdRules = computed(() => [
    requiredRule('userProfile.enterOldPassword')
  ]);

  const pwdRules = computed(() => [
    requiredRule('accountHandling.changePassword.rules.enterPassword'),
    structureRule
  ]);

  const emailLoginRules = (translationKey?: string) => [
    requiredRule(translationKey || 'login.rules.login'),
    (v: string) =>
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) ||
      t('login.rules.validEmailAddress')
  ];

  const otpRules = computed(() => [
    requiredRule('login.mfa.rules.empty'),
    (v: string) => (v && v.length === 6) || t('login.mfa.rules.enterValidCode')
  ]);

  return {
    confirmPasswordRules,
    differentPwdRules,
    oldPwdRules,
    pwdRules,
    otpRules,
    recommendPasswordRules: RECOMMENDED_PASSWORD_RULES,
    requiredRule,
    emailLoginRules
  };
};
